import app, { db } from '../firebase';
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc,
  collection, getDocs, query, orderBy, writeBatch
} from 'firebase/firestore';
import {
  getStorage, ref, uploadBytes, getDownloadURL, deleteObject
} from 'firebase/storage';

const storage = getStorage(app);

// Firestore path note: the spec path "teamStructure/agents/{agentId}" isn't a valid
// Firestore document path (collection/doc/collection/doc requires 4 segments, not 3).
// Implemented as its own top-level collection instead, sibling to teamStructure/founder —
// see report for details.
const AGENTS_COLLECTION = 'teamStructureAgents';

export async function getFounder() {
  try {
    const snap = await getDoc(doc(db, 'teamStructure', 'founder'));
    return snap.exists() ? { ...snap.data(), id: 'founder' } : null;
  } catch (e) { console.error(e); throw e; }
}

export async function updateFounder(data) {
  try {
    await setDoc(doc(db, 'teamStructure', 'founder'), { ...data, updatedAt: Date.now() }, { merge: true });
  } catch (e) { console.error(e); throw e; }
}

// `photoBlob` is expected to already be a cropped/sized JPEG (the UI's drag-to-reposition
// cropper produces this at a fixed 1080x1080 before calling here) — no server-side resizing.
export async function uploadTeamMemberPhoto(docId, photoBlob) {
  try {
    const storageRef = ref(storage, `teamStructure/${docId}/photo.jpg`);
    await uploadBytes(storageRef, photoBlob, { contentType: 'image/jpeg' });
    return await getDownloadURL(storageRef);
  } catch (e) { console.error(e); throw e; }
}

export async function getAgents() {
  try {
    const q = query(collection(db, AGENTS_COLLECTION), orderBy('order', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ ...d.data(), id: d.id }));
  } catch (e) { console.error(e); throw e; }
}

export async function addAgent(data) {
  try {
    const existing = await getDocs(collection(db, AGENTS_COLLECTION));
    const nextOrder = existing.docs.reduce((max, d) => Math.max(max, d.data().order ?? -1), -1) + 1;
    const agentId = `agent_${Date.now()}`;
    const agent = { ...data, order: nextOrder, createdAt: Date.now(), updatedAt: Date.now() };
    await setDoc(doc(db, AGENTS_COLLECTION, agentId), agent);
    return { ...agent, id: agentId };
  } catch (e) { console.error(e); throw e; }
}

export async function updateAgent(agentId, data) {
  try {
    await updateDoc(doc(db, AGENTS_COLLECTION, agentId), { ...data, updatedAt: Date.now() });
  } catch (e) { console.error(e); throw e; }
}

// Hard delete: removes the Firestore doc and its Storage photo (if any).
export async function deleteAgent(agentId) {
  try {
    await deleteDoc(doc(db, AGENTS_COLLECTION, agentId));
    try {
      await deleteObject(ref(storage, `teamStructure/${agentId}/photo.jpg`));
    } catch (e) {
      if (e.code !== 'storage/object-not-found') throw e;
    }
  } catch (e) { console.error(e); throw e; }
}

export async function reorderAgents(orderedIdList) {
  try {
    const batch = writeBatch(db);
    orderedIdList.forEach((agentId, index) => {
      batch.update(doc(db, AGENTS_COLLECTION, agentId), { order: index, updatedAt: Date.now() });
    });
    await batch.commit();
  } catch (e) { console.error(e); throw e; }
}
